import { Difficulty } from "../Difficulty";
import { MixPuzzle } from "../mixpuzzles/types";
import { Looop, LooopAudioFiles, LooopInstrument } from "../Looop";
import { Game } from "./Game";

interface QuestionBase {
  difficulty: Difficulty;
  roundId: string;
  todoId: number | null;
}

/**
 * Mix Puzzle
 */

export interface GameQuestionMixPuzzle extends QuestionBase {
  type: Game.mixPuzzle;
  mixPuzzle: MixPuzzle;
}

export function isGameQuestionMixPuzzle(
  question: GameQuestion
): question is GameQuestionMixPuzzle {
  return question.type === Game.mixPuzzle;
}

/**
 * Frequency Finder
 */

export interface GameQuestionFrequencyFinder extends QuestionBase {
  type:
    | Game.freq_finder_boost
    | Game.freq_finder_cut
    | Game.freq_finder_hipass
    | Game.freq_finder_lopass;
  frequency: number;
  instrument: LooopInstrument;
  looop: Looop;
}

export function isGameQuestionFrequencyFinder(
  question: GameQuestion
): question is GameQuestionFrequencyFinder {
  return (
    question.type === "freq_finder_boost" ||
    question.type === "freq_finder_cut" ||
    question.type === "freq_finder_hipass" ||
    question.type === "freq_finder_lopass"
  );
}

/**
 * Is This Compressor On?
 */

export interface GameQuestionIsThisCompressorOn extends QuestionBase {
  type: Game.is_this_compressor_on;
  instrument: LooopInstrument;
  looop: Looop;
  answer: IsThisCompressorOnAnswerOptions;
  options: {
    a: keyof LooopAudioFiles;
    b: keyof LooopAudioFiles;
  };
}

export type IsThisCompressorOnAnswerOptions =
  | keyof GameQuestionIsThisCompressorOn["options"]
  | "neither";

export function isGameQuestionIsThisCompressorOn(
  question: GameQuestion
): question is GameQuestionIsThisCompressorOn {
  return question.type === Game.is_this_compressor_on;
}

/**
 * Fader Grader
 */

export interface GameQuestionFaderGrader extends QuestionBase {
  type: Game.fader_grader;
  instrument: LooopInstrument;
  looop: Looop;
  volume: number;
  minVolume: number;
  maxVolume: number;
  steps: number;
}

/**
 * Can You Pan?
 */

export interface GameQuestionCanYouPan extends QuestionBase {
  type: Game.can_you_pan;
  instrument: LooopInstrument;
  looop: Looop;
  panDegree: number;
  panDegreeSteps: number;
}

/**
 * Game Question
 */

export type GameQuestion =
  | GameQuestionFrequencyFinder
  | GameQuestionMixPuzzle
  | GameQuestionIsThisCompressorOn
  | GameQuestionFaderGrader
  | GameQuestionCanYouPan;

/**
 * Helpers
 */

export function getLooopFromGameQuestion(question: GameQuestion): Looop {
  if (isGameQuestionMixPuzzle(question)) {
    return question.mixPuzzle.looop;
  }

  return question.looop;
}
